import { c as createFocusTrap } from './focus-trap.esm-MYAhwz8v.js';

class HeaderNavigation extends HTMLElement {
  /** @type {NodeListOf<HTMLElement>} */ #controllers = this.querySelectorAll(
    '[aria-controls^="header-navigation--"]'
  );
  /** @type {HTMLElement|null} */ #activeItem;
  /** @type {AbortController|null} */ #scrollController = null;
  /** @type {import('focus-trap').FocusTrap} */ #focusTrap;
  /** @type {MegaMenuElement|null} */ #megaMenuElement;
  /** @type {number} */ #closingTimeout;
  /** @type {AbortController|null} */ #pointerController = null;
  /** @type {{ controller: boolean, target: boolean }} */ #pointerOver = {
    controller: false,
    target: false,
  };

  connectedCallback() {
    this.#controllers.forEach((controller) => {
      controller.addEventListener('mouseenter', () => this.#handleMouseenter(controller));
      controller.addEventListener('mouseleave', (event) =>
        this.#handleMouseleave(event, controller)
      );
      controller.addEventListener('keydown', (event) => this.#handleKeydown(event, controller));
    });

    this.#megaMenuElement = this.querySelector('mega-menu-element');
  }

  /**
   * Handle controller mouseenter
   * @param {HTMLElement} controller
   */
  #handleMouseenter(controller) {
    const target = controller.ariaControlsElements[0];
    if (!target) return;

    this.#pointerOver.controller = true;
    clearTimeout(this.#closingTimeout);
    this.#open(controller, target);
  }

  /**
   * Handle controller mouseleave
   * @param {MouseEvent} event
   * @param {HTMLElement} controller
   */
  #handleMouseleave(event, controller) {
    const target = controller.ariaControlsElements[0];
    if (!target) return;

    const nextElement = event.relatedTarget;
    this.#pointerOver.controller = false;

    // Moving from the controller into the target should not close the panel.
    if (
      nextElement instanceof Element &&
      (controller.contains(nextElement) || target.contains(nextElement))
    ) {
      return;
    }

    // Wait one frame so a controller→target move through a gap can register on the target.
    requestAnimationFrame(() => {
      if (this.#pointerOver.controller || this.#pointerOver.target) return;

      this.#prepareClose();
    });
  }

  /**
   * Handle controller keydown
   * @param {KeyboardEvent} event
   * @param {HTMLElement} controller
   */
  #handleKeydown(event, controller) {
    if (event.code !== 'Space') return;

    // Prevent default space key behavior
    event.preventDefault();

    const target = controller.ariaControlsElements[0];
    if (!target) return;

    // If the active item is open, close it, otherwise open it
    this.#activeItem ? this.#close(controller, target) : this.#open(controller, target);

    // Create the focus trap
    this.#focusTrap = createFocusTrap(target, {
      onDeactivate: () => this.#close(controller, target),
    });

    // Activate the focus trap once the transition ends
    target.addEventListener('transitionend', () => this.#focusTrap.activate(), { once: true });
  }

  /**
   * Open the active item
   * @param {HTMLElement} controller
   * @param {HTMLElement} target
   */
  #open(controller, target) {
    if (this.#activeItem !== null && this.#activeItem !== undefined) {
      this.#close(this.#activeItem.controller, this.#activeItem.target);
    }

    this.#activeItem = { controller, target };

    controller.ariaExpanded = 'true';
    target.ariaHidden = 'false';

    clearTimeout(this.#closingTimeout);
    this.#setupTargetMouseEvents(controller, target);

    // Add scroll listener
    this.#addScrollListener(controller, target);
  }

  /**
   * Close the active item
   * @param {HTMLElement} controller
   * @param {HTMLElement} target
   */
  #close(controller, target) {
    this.#activeItem = null;

    clearTimeout(this.#closingTimeout);
    this.#pointerController?.abort();
    this.#pointerController = null;
    this.#pointerOver = { controller: false, target: false };

    controller.ariaExpanded = 'false';
    target.ariaHidden = 'true';

    //  Collapse all panels. Ref: mega-menu.js:43
    this.#megaMenuElement?.collapseAll?.();

    // Clean up scroll listener
    this.#scrollController?.abort();
    this.#scrollController = null;

    // Clean up focus trap
    if (this.#focusTrap) this.#focusTrap = null;
  }

  /**
   * Prepare to close the active item, but only close it after a delay (if close is still appropriate)
   */
  #prepareClose() {
    clearTimeout(this.#closingTimeout);
    this.#closingTimeout = setTimeout(() => {
      if (!this.#activeItem) return;
      if (this.#pointerOver.controller || this.#pointerOver.target) return;

      const { controller, target } = this.#activeItem;
      this.#close(controller, target);
    }, 150);
  }

  /**
   * Track pointer presence on the target.
   * Used for properly closing dropdown/menu items after a delay.
   * @param {HTMLElement} controller
   * @param {HTMLElement} target
   */
  #setupTargetMouseEvents(controller, target) {
    this.#pointerController?.abort();
    this.#pointerController = new AbortController();
    const { signal } = this.#pointerController;

    target.addEventListener(
      'mouseenter',
      () => {
        this.#pointerOver.target = true;
        clearTimeout(this.#closingTimeout);
      },
      { signal }
    );

    target.addEventListener(
      'mouseleave',
      (event) => {
        this.#pointerOver.target = false;

        const nextElement = event.relatedTarget;
        if (nextElement instanceof Element && controller.contains(nextElement)) return;

        this.#prepareClose();
      },
      { signal }
    );
  }

  /**
   * Add scroll listener to the active item
   * @param {HTMLElement} controller
   * @param {HTMLElement} target
   */
  #addScrollListener(controller, target) {
    // Clean up any existing scroll listener
    this.#scrollController?.abort();

    this.#scrollController = new AbortController();
    window.addEventListener('scroll', () => this.#close(controller, target), {
      once: true,
      signal: this.#scrollController.signal,
    });
  }
}

customElements.define('header-navigation', HeaderNavigation);
